import { AxiosError } from 'axios';

import { SSODiscoveryResponse, SSOStatusResponse } from '../types';
import { client } from './index';

// Generate a secure random hex string of 32 characters
const generateSecureRandomHex = (): string => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const discoverSSOPool = async (
  identifier: string,
): Promise<
  | SSODiscoveryResponse
  | {
      error: string;
      success: boolean;
    }
> => {
  try {
    const secureRandomHex = generateSecureRandomHex();

    const response = await client.post<SSODiscoveryResponse>('/sso/discover', {
      identifier,
      state: secureRandomHex, // Include the secure random hex as state parameter
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 429) {
        return {
          success: false,
          error: 'TOO_MANY_ATTEMPTS',
        };
      }

      if (axiosError.response?.status === 404) {
        return {
          success: false,
          error: 'INVALID_IDENTIFIER',
        };
      }

      return {
        success: false,
        error: (axiosError.response?.data as { error?: string })?.error || 'FAILED_VALIDATION',
      };
    }

    return {
      success: false,
      error: 'FAILED_VALIDATION',
    };
  }
};

const checkSSOStatus = async (): Promise<SSOStatusResponse> => {
  try {
    const response = await client.get<SSOStatusResponse>('/sso/status');
    return response.data;
  } catch (error) {
    console.error('SSO status check failed:', error);
    return {
      authenticated: false,
      error: 'FAILED_STATUS_CHECK',
    };
  }
};

const logoutSSO = async () => {
  try {
    await client.get('/sso/logout');
    localStorage.removeItem('sso_authenticated');
    localStorage.removeItem('sso_user');
  } catch (error) {
    console.log('SSO logout failed:', error);
  }
};

export const SSOApi = {
  discoverSSOPool,
  checkSSOStatus,
  logoutSSO,
};
